import { useMemo, useState } from 'react';
import { Download, Eye, History, Landmark, ShieldAlert } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { Notificacao } from '@/ui/Notificacao';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { MOCK_EMPENHOS } from './dados-simulados';
import { useEmpenhosSof, useExportarLoteSof, useLotesSof } from './hooks/useCiclo';
import { exportarLoteSofPdf } from './sof/exportarLoteSof';
import { SofHeader } from './sof/SofHeader';
import { SofTabsNav } from './sof/SofTabsNav';
import type { EmpenhoLinha, LoteSofLinha, StatusEmpenho, StatusLoteSof } from './types';

const ROTULO_STATUS_LOTE: Record<StatusLoteSof, string> = {
  RASCUNHO: 'Rascunho',
  CONSOLIDADO: 'Consolidado',
  ENVIADO_SOF: 'Enviado à SOF',
  CONFIRMADO_SOF: 'Confirmado',
  REJEITADO_SOF: 'Rejeitado',
  CANCELADO: 'Cancelado',
};

const ROTULO_STATUS_EMPENHO: Record<StatusEmpenho, string> = {
  PRE_EMPENHO: 'Pré-empenho',
  EMPENHADO: 'Empenhado',
  ANULADO: 'Anulado',
};

function opcoesMes(): { value: string; label: string }[] {
  const hoje = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const competencia = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const rotulo = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return { value: competencia, label: rotulo.charAt(0).toUpperCase() + rotulo.slice(1) };
  });
}

const MESES = opcoesMes();

function dentroDoIntervalo(dataIso: string | null, intervalo: DateRangeValue): boolean {
  if (!dataIso) return false;
  const { from, to } = resolveDateRange(intervalo);
  const data = dataIso.slice(0, 10);
  if (from && data < from) return false;
  if (to && data > to) return false;
  return true;
}

/**
 * FIN-030/031: hub SOF, aba "Lotes SOF" — 3 sub-abas iguais ao protótipo
 * (`ExportSofPage.tsx`): Exportar (lotes ainda não enviados), Empenhos
 * (notas de empenho vinculadas) e Histórico (lotes já enviados). Sem
 * `PageHeader`/filtro de competência no topo — o protótipo também não tem,
 * porque esta tela vive embutida no hub `/financeiro/sof`; cada aba tem seu
 * próprio filtro de mês, igual ao original (`exportMes`/`logMesFilter`).
 */
export function SofPage() {
  const [aba, definirAba] = useState<'exportar' | 'empenhos' | 'historico'>('exportar');
  const { data, isLoading, isError, refetch } = useLotesSof({ page: 1, perPage: 200 });
  const { data: empenhos } = useEmpenhosSof();
  const exportar = useExportarLoteSof();

  const [buscaExportar, definirBuscaExportar] = useState('');
  const [mesExportar, definirMesExportar] = useState('');
  const [statusExportar, definirStatusExportar] = useState('');
  const [periodoExportar, definirPeriodoExportar] = useState<DateRangeValue>({
    preset: 'THIS_MONTH',
  });

  const [buscaEmpenho, definirBuscaEmpenho] = useState('');
  const [statusEmpenho, definirStatusEmpenho] = useState('');

  const [mesHistorico, definirMesHistorico] = useState('');
  const [periodoHistorico, definirPeriodoHistorico] = useState<DateRangeValue>({ preset: 'ALL' });

  const [loteVisualizado, definirLoteVisualizado] = useState<LoteSofLinha | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);

  const lotes: LoteSofLinha[] = useMemo(() => data?.dados ?? [], [data]);

  const lotesParaExportar = useMemo(() => {
    let base = lotes.filter((l) => !l.enviadoSofEm);
    if (mesExportar) base = base.filter((l) => l.mesReferencia.startsWith(mesExportar));
    else base = base.filter((l) => dentroDoIntervalo(l.mesReferencia, periodoExportar));
    if (statusExportar) base = base.filter((l) => l.status === statusExportar);
    if (buscaExportar) {
      const termo = buscaExportar.toLowerCase();
      base = base.filter(
        (l) =>
          (l.numeroLote ?? '').toLowerCase().includes(termo) ||
          (l.dreNome ?? '').toLowerCase().includes(termo),
      );
    }
    return base;
  }, [lotes, buscaExportar, mesExportar, statusExportar, periodoExportar]);

  const lotesHistorico = useMemo(() => {
    let base = lotes.filter((l) => !!l.enviadoSofEm);
    if (mesHistorico) base = base.filter((l) => l.mesReferencia.startsWith(mesHistorico));
    else base = base.filter((l) => dentroDoIntervalo(l.enviadoSofEm, periodoHistorico));
    return [...base].sort((a, b) => (b.enviadoSofEm ?? '').localeCompare(a.enviadoSofEm ?? ''));
  }, [lotes, mesHistorico, periodoHistorico]);

  const empenhosFiltrados = useMemo(() => {
    let base = empenhos ?? [];
    if (statusEmpenho) base = base.filter((e) => e.status === statusEmpenho);
    if (buscaEmpenho) {
      const termo = buscaEmpenho.toLowerCase();
      base = base.filter(
        (e) =>
          e.numeroNe.toLowerCase().includes(termo) ||
          e.credenciadoNome.toLowerCase().includes(termo),
      );
    }
    return base;
  }, [empenhos, buscaEmpenho, statusEmpenho]);

  const empenhosDoLote = (loteId: string): EmpenhoLinha[] =>
    MOCK_EMPENHOS.filter((e) => e.loteId === loteId);

  async function confirmarExportacao() {
    if (!loteVisualizado) return;
    try {
      exportarLoteSofPdf(loteVisualizado, empenhosDoLote(loteVisualizado.id));
      await exportar.mutateAsync(loteVisualizado.id);
      definirAviso(`Lote ${loteVisualizado.numeroLote ?? loteVisualizado.id} exportado à SOF.`);
      definirLoteVisualizado(null);
    } catch {
      definirAviso('Não foi possível exportar o lote. Tente novamente.');
    }
  }

  const headerSubtitulo = useMemo(() => {
    if (aba === 'exportar') {
      const total = lotesParaExportar.length;
      const valor = lotesParaExportar.reduce((s, l) => s + parseFloat(l.valorTotal), 0);
      return (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>
            Geração de{' '}
            <GlossaryTerm termo="LOTE_SOF" esconderIcone>
              lotes SOF
            </GlossaryTerm>{' '}
            a partir de medições consolidadas.
          </span>
          {total > 0 && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>
                <strong className="tabular-nums text-foreground">{total}</strong> lote(s)
                pendente(s)
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="tabular-nums">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                  valor,
                )}
              </span>
            </>
          )}
        </span>
      );
    }
    if (aba === 'empenhos') {
      const total = (empenhos ?? []).length;
      const empenhados = (empenhos ?? []).filter((e) => e.status === 'EMPENHADO').length;
      return (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>
            <GlossaryTerm termo="EMPENHO" esconderIcone>
              Empenhos
            </GlossaryTerm>{' '}
            e{' '}
            <GlossaryTerm termo="PRE_EMPENHO" esconderIcone>
              pré-empenhos
            </GlossaryTerm>{' '}
            registrados no SOF.
          </span>
          {total > 0 && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>
                <strong className="tabular-nums text-foreground">{total}</strong> NE(s)
              </span>
              {empenhados > 0 && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-medium text-success">{empenhados} empenhado(s)</span>
                </>
              )}
            </>
          )}
        </span>
      );
    }
    return <span>Histórico imutável de lotes exportados ao SOF — disponível para auditoria.</span>;
  }, [aba, lotesParaExportar, empenhos]);

  const colunasLote: DataTableColumn<LoteSofLinha & { id: string }>[] = [
    { header: 'Lote', accessorKey: 'numeroLote', sortKey: 'numeroLote' },
    {
      header: 'Competência',
      sortKey: 'mesReferencia',
      cell: (linha) =>
        new Date(linha.mesReferencia).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    { header: 'DRE', cell: (linha) => linha.dreNome ?? 'Todas as DREs' },
    {
      header: 'Credenciados',
      accessorKey: 'totalCredenciados',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Empenhos',
      accessorKey: 'totalEmpenhos',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Valor',
      cell: (linha) => <MoneyDisplay value={linha.valorTotal} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      sortKey: (linha) => parseFloat(linha.valorTotal),
    },
    {
      header: 'Status',
      sortKey: 'status',
      cell: (linha) => (
        <Badge variant={linha.status === 'CONFIRMADO_SOF' ? 'default' : 'secondary'}>
          {ROTULO_STATUS_LOTE[linha.status]}
        </Badge>
      ),
    },
  ];

  const colunasExportar: DataTableColumn<LoteSofLinha & { id: string }>[] = [
    ...colunasLote,
    {
      header: 'Ação',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (linha) => (
        <Button variant="outline" size="sm" onClick={() => definirLoteVisualizado(linha)}>
          <Eye className="mr-1 h-4 w-4" aria-hidden="true" /> Visualizar
        </Button>
      ),
    },
  ];

  const colunasHistorico: DataTableColumn<LoteSofLinha & { id: string }>[] = [
    ...colunasLote,
    {
      header: 'Enviado em',
      sortKey: (linha) => (linha.enviadoSofEm ? new Date(linha.enviadoSofEm).getTime() : 0),
      cell: (linha) =>
        linha.enviadoSofEm
          ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
              new Date(linha.enviadoSofEm),
            )
          : '—',
    },
  ];

  const colunasEmpenho: DataTableColumn<EmpenhoLinha & { id: string }>[] = [
    {
      header: 'Nº NE',
      accessorKey: 'numeroNe',
      cellClassName: 'font-mono text-sm font-medium',
      sortKey: 'numeroNe',
    },
    {
      header: 'Dotação orçamentária',
      accessorKey: 'dotacaoOrcamentaria',
      cellClassName: 'font-mono text-xs text-muted-foreground',
    },
    { header: 'Credenciado', accessorKey: 'credenciadoNome', sortKey: 'credenciadoNome' },
    {
      header: 'Mês Ref.',
      sortKey: 'mesReferencia',
      cell: (linha) =>
        new Date(linha.mesReferencia).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      header: 'Valor',
      cell: (linha) => <MoneyDisplay value={linha.valor} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      sortKey: (linha) => parseFloat(linha.valor),
    },
    {
      header: 'Status',
      sortKey: 'status',
      cell: (linha) => (
        <Badge
          variant={
            linha.status === 'EMPENHADO'
              ? 'default'
              : linha.status === 'ANULADO'
                ? 'destructive'
                : 'secondary'
          }
        >
          {ROTULO_STATUS_EMPENHO[linha.status]}
        </Badge>
      ),
    },
  ];

  const camposExportar: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por lote ou DRE…',
      flex: true,
    },
    {
      key: 'mes',
      label: 'Mês de referência',
      type: 'select',
      placeholder: 'Todos os meses',
      allOptionLabel: 'Todos os meses',
      options: MESES,
      width: 'w-full sm:w-[200px]',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Status',
      allOptionLabel: 'Todos os status',
      options: [
        { value: 'RASCUNHO', label: 'Rascunho' },
        { value: 'CONSOLIDADO', label: 'Consolidado' },
      ],
      width: 'w-full sm:w-[180px]',
    },
  ];

  const camposEmpenho: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por NE ou credenciado…',
      flex: true,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      allOptionLabel: 'Todos os status',
      options: [
        { value: 'PRE_EMPENHO', label: 'Pré-empenho' },
        { value: 'EMPENHADO', label: 'Empenhado' },
        { value: 'ANULADO', label: 'Anulado' },
      ],
    },
  ];

  const camposHistorico: FilterFieldConfig[] = [
    {
      key: 'mes',
      label: 'Mês de referência',
      type: 'select',
      placeholder: 'Todos os meses',
      allOptionLabel: 'Todos os meses',
      options: MESES,
      width: 'w-full sm:w-[200px]',
    },
  ];

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      {aviso && <Notificacao tom="sucesso" mensagem={aviso} aoFechar={() => definirAviso(null)} />}

      <p className="text-sm text-muted-foreground">{headerSubtitulo}</p>

      <div className="flex items-center gap-2 rounded-lg border border-warning/50 bg-warning/15 px-3 py-2 text-sm text-foreground">
        <ShieldAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
        <span className="font-medium">Integração SOF em modo simulação.</span>
        <span className="hidden text-muted-foreground sm:inline">
          Layout de referência — envio e confirmação manuais.
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto text-xs font-medium underline-offset-2 hover:underline"
            >
              Saiba mais
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-w-sm space-y-2 text-sm">
            <p className="font-semibold">Integração SOF em modo simulação</p>
            <p className="text-muted-foreground">
              O envio aqui gera um PDF de referência e marca o lote como enviado. O canal técnico
              oficial (API, SFTP ou planilha) e a confirmação automática de retorno da SOF ainda não
              existem — até lá, "Enviado" e "Confirmado" são marcados manualmente com base no
              protocolo recebido por outros meios.
            </p>
          </PopoverContent>
        </Popover>
      </div>

      <Tabs value={aba} onValueChange={(v) => definirAba(v as typeof aba)}>
        <TabsList>
          <TabsTrigger value="exportar">
            <Download className="mr-2 h-4 w-4" aria-hidden="true" /> Exportar
          </TabsTrigger>
          <TabsTrigger value="empenhos">
            <Landmark className="mr-2 h-4 w-4" aria-hidden="true" /> Empenhos
          </TabsTrigger>
          <TabsTrigger value="historico">
            <History className="mr-2 h-4 w-4" aria-hidden="true" /> Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exportar" className="space-y-4">
          <FilterBar
            fields={camposExportar}
            values={{ busca: buscaExportar, mes: mesExportar, status: statusExportar }}
            onChange={(chave, valor) => {
              if (chave === 'busca') definirBuscaExportar(valor);
              if (chave === 'mes') definirMesExportar(valor);
              if (chave === 'status') definirStatusExportar(valor);
            }}
            onClear={() => {
              definirBuscaExportar('');
              definirMesExportar('');
              definirStatusExportar('');
              definirPeriodoExportar({ preset: 'THIS_MONTH' });
            }}
            hasActiveFilters={
              !!buscaExportar ||
              !!mesExportar ||
              !!statusExportar ||
              periodoExportar.preset !== 'THIS_MONTH'
            }
          >
            <DateRangePresetFilter
              label="Período de competência"
              value={periodoExportar}
              onChange={definirPeriodoExportar}
              width="w-full sm:w-[220px]"
            />
          </FilterBar>

          {mesExportar && (
            <p className="-mt-2 text-xs text-muted-foreground">
              Mês específico selecionado — período ignorado.
            </p>
          )}

          {isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
            />
          ) : (
            <Card>
              <CardHeader className="pb-0">
                <p className="text-base font-semibold">Lotes consolidados para exportação</p>
                <p className="text-sm text-muted-foreground">
                  Selecione um lote para gerar o PDF e enviar à SOF
                </p>
              </CardHeader>
              <CardContent className="p-0 pt-4">
                <DataTable
                  columns={colunasExportar}
                  data={lotesParaExportar.map((l) => ({ ...l, id: l.id }))}
                  isLoading={isLoading}
                  emptyTitle="Nenhum lote pendente de exportação"
                  emptyDescription="Todos os lotes do filtro selecionado já foram enviados à SOF."
                  ariaLabel="Lotes pendentes de exportação"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="empenhos" className="space-y-4">
          <FilterBar
            fields={camposEmpenho}
            values={{ busca: buscaEmpenho, status: statusEmpenho }}
            onChange={(chave, valor) => {
              if (chave === 'busca') definirBuscaEmpenho(valor);
              if (chave === 'status') definirStatusEmpenho(valor);
            }}
            onClear={() => {
              definirBuscaEmpenho('');
              definirStatusEmpenho('');
            }}
          />
          <Card>
            <CardHeader className="pb-0">
              <p className="text-base font-semibold">Notas de Empenho</p>
              <p className="text-sm text-muted-foreground">
                Empenhos vinculados às medições financeiras
              </p>
            </CardHeader>
            <CardContent className="p-0 pt-4">
              <DataTable
                columns={colunasEmpenho}
                data={empenhosFiltrados.map((e) => ({ ...e, id: e.id }))}
                emptyTitle="Nenhum empenho encontrado"
                emptyDescription={
                  buscaEmpenho || statusEmpenho
                    ? 'Ajuste os filtros para ver outros empenhos.'
                    : 'Não há empenhos registrados.'
                }
                ariaLabel="Notas de empenho"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico" className="space-y-4">
          <FilterBar
            fields={camposHistorico}
            values={{ mes: mesHistorico }}
            onChange={(_, valor) => definirMesHistorico(valor)}
            onClear={() => {
              definirMesHistorico('');
              definirPeriodoHistorico({ preset: 'ALL' });
            }}
          >
            <DateRangePresetFilter
              label="Período de envio"
              value={periodoHistorico}
              onChange={definirPeriodoHistorico}
              width="w-full sm:w-[220px]"
            />
          </FilterBar>
          <Card>
            <CardContent className="p-0">
              <DataTable
                columns={colunasHistorico}
                data={lotesHistorico.map((l) => ({ ...l, id: l.id }))}
                emptyTitle="Nenhuma exportação registrada"
                emptyDescription="O histórico aparece aqui depois do primeiro lote enviado à SOF."
                ariaLabel="Histórico de lotes exportados"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ResponsiveModal
        open={!!loteVisualizado}
        onOpenChange={(aberto) => !aberto && definirLoteVisualizado(null)}
        title={`Lote ${loteVisualizado?.numeroLote ?? ''}`}
        description={
          loteVisualizado
            ? `${loteVisualizado.dreNome ?? 'Todas as DREs'} · ${loteVisualizado.totalCredenciados} credenciado(s) · ${loteVisualizado.totalEmpenhos} empenho(s)`
            : undefined
        }
      >
        {loteVisualizado && (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Nº NE</th>
                    <th className="px-3 py-2 text-left">Credenciado</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {empenhosDoLote(loteVisualizado.id).map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{e.numeroNe}</td>
                      <td className="px-3 py-2">{e.credenciadoNome}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        <MoneyDisplay value={e.valor} />
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant={
                            e.status === 'EMPENHADO'
                              ? 'default'
                              : e.status === 'ANULADO'
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {ROTULO_STATUS_EMPENHO[e.status]}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {empenhosDoLote(loteVisualizado.id).length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        Nenhum empenho de exemplo cadastrado para este lote.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="font-semibold">Total do lote</span>
              <span className="text-lg font-bold">
                <MoneyDisplay value={loteVisualizado.valorTotal} />
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => definirLoteVisualizado(null)}>
                Cancelar
              </Button>
              <Button onClick={() => void confirmarExportacao()} disabled={exportar.isPending}>
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                {exportar.isPending ? 'Exportando…' : 'Exportar PDF e enviar à SOF'}
              </Button>
            </div>
          </div>
        )}
      </ResponsiveModal>
    </div>
  );
}

export default SofPage;
